// filepath: apps/web/src/features/promote/useApprovePromotion.ts
//
// FR-UI-PROM-1 AC-4: TanStack Query mutation for POST /api/admin/promotion-reviews/{id}/approve.
// The confirm_irreversible flag is required when the plan contains irreversible field-promotion
// contract steps (FR-ENT-4).

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import { promoteKeys } from "./usePromotionReview";

interface ApproveVariables {
  reviewId: string;
  confirmIrreversible: boolean;
}

export function useApprovePromotion() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, confirmIrreversible }: ApproveVariables) => {
      return apiFetch(`/api/admin/promotion-reviews/${reviewId}/approve`, {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idempotency_key: crypto.randomUUID(),
            confirm_irreversible: confirmIrreversible,
          }),
        },
        idempotencyKey: true,
      }).then((r) => {
        if (!r.ok) {
          // Parse error envelope if available.
          r.json().catch(() => null);
          throw new ApiError(r.statusText, r.status, `http_${r.status}`);
        }
        return r.json();
      });
    },
    onSuccess: (_data: unknown, variables: ApproveVariables) => {
      queryClient.invalidateQueries({
        queryKey: promoteKeys.reviewContext(tenantSlug, variables.reviewId),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}
